import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";

function formatBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let x = v;
  let i = -1;
  do {
    x /= 1024;
    i += 1;
  } while (x >= 1024 && i < units.length - 1);
  return `${x.toFixed(x >= 10 ? 0 : 1)} ${units[i]}`;
}

function CheckPill({ ok, label }) {
  return (
    <span className={`maint-pill ${ok ? "ok" : "bad"}`} title={label}>
      {ok ? "OK" : "FALLA"} · {label}
    </span>
  );
}

function DiskCard({ disk }) {
  return (
    <div className="maint-disk-card">
      <h4>{disk.label}</h4>
      <p className="admin-storage-path">{disk.path || "—"}</p>
      {disk.mounted && disk.total_bytes > 0 ? (
        <>
          <div
            className={`admin-storage-bar ${disk.percent_used >= 85 ? "warn" : ""}`}
            title={`${disk.percent_used}% usado`}
          >
            <i style={{ width: `${Math.min(100, disk.percent_used)}%` }} />
          </div>
          <p className="maint-meta">
            {formatBytes(disk.used_bytes)} usados · {formatBytes(disk.free_bytes)} libres ·{" "}
            {formatBytes(disk.total_bytes)} total ({disk.percent_used}%)
          </p>
        </>
      ) : (
        <p className="admin-storage-offline">{disk.hint || "No montado o sin acceso"}</p>
      )}
    </div>
  );
}

export default function MaintenancePanel({ onMessage, onError }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [localError, setLocalError] = useState("");
  const [localOk, setLocalOk] = useState("");

  const notify = useCallback(
    (okMsg, errMsg) => {
      if (okMsg) {
        setLocalOk(okMsg);
        onMessage?.(okMsg);
      }
      if (errMsg) {
        setLocalError(errMsg);
        onError?.(errMsg);
      }
    },
    [onMessage, onError]
  );

  const refresh = useCallback(() => {
    return api("/api/admin/maintenance", { token })
      .then((payload) => {
        setData(payload);
        setLocalError("");
      })
      .catch((err) => {
        setLocalError(err.message || "No se pudo cargar mantenimiento");
      });
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    refresh().catch(() => {});
    const timer = setInterval(() => {
      if (!cancelled) refresh().catch(() => {});
    }, 8000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [refresh]);

  async function runAction(key, fn, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(key);
    setLocalError("");
    setLocalOk("");
    try {
      const result = await fn();
      await refresh();
      return result;
    } catch (err) {
      notify(null, err.message || "Acción fallida");
      return null;
    } finally {
      setBusy("");
    }
  }

  async function purgeTmp({ onlyOrphans, kinds, confirmText, busyKey }) {
    return runAction(
      busyKey,
      async () => {
        const r = await api("/api/admin/tmp/purge", {
          method: "POST",
          token,
          body: { only_orphans: onlyOrphans, kinds },
        });
        notify(
          `Borradas ${r.deleted || 0} carpetas · liberados ${formatBytes(r.freed_bytes)}${
            r.skipped ? ` · ${r.skipped} omitidas (activas)` : ""
          }.`,
          null
        );
        return r;
      },
      confirmText
    );
  }

  const health = data?.health;
  const queue = data?.queue || {};
  const tmp = data?.tmp || health?.tmp || [];
  const disks = data?.disks || [];
  const activePool = data?.storage?.active_pool;
  const items = queue.items || [];
  const stuck = items.filter((it) => it.stuck);
  const counts = queue.db_counts || {};
  const tmpTotalBytes = tmp.reduce((acc, t) => acc + (t.bytes || 0), 0);
  const tmpOrphanBytes = tmp.reduce(
    (acc, t) => acc + (t.exists ? Math.round((t.bytes || 0) * ((t.orphan_dirs || 0) / Math.max(1, t.video_dirs || 1))) : 0),
    0
  );

  return (
    <div className="maint-panel">
      <div className="hint-panel">
        <p>
          Centro de <strong>mantenimiento de la plataforma</strong>: revisa discos, limpia
          temporales de transcode, destraba la cola HLS y libera espacio en MinIO.
        </p>
        <p className="muted" style={{ marginBottom: 0 }}>
          Las acciones destructivas piden confirmación. Nunca se borran carpetas de vídeos en
          cola o procesando.
        </p>
      </div>

      {localOk ? <p className="ok">{localOk}</p> : null}
      {localError ? <p className="error">{localError}</p> : null}

      <section className="maint-section">
        <div className="maint-section-head">
          <h3>
            Discos y almacenamiento
            {activePool ? (
              <span className="muted" style={{ fontWeight: 400, fontSize: "0.85rem" }}>
                {" "}
                · pool activo: <strong>{activePool}</strong>
              </span>
            ) : null}
          </h3>
          <button type="button" className="ghost" disabled={!!busy} onClick={() => refresh()}>
            Actualizar
          </button>
        </div>
        <div className="maint-disk-grid">
          {disks.length ? (
            disks.map((disk) => <DiskCard key={`${disk.kind}-${disk.path}`} disk={disk} />)
          ) : (
            <p className="muted">Cargando discos…</p>
          )}
        </div>
      </section>

      <section className="maint-section">
        <div className="maint-section-head">
          <h3>Salud del sistema</h3>
        </div>
        <div className="maint-pills">
          {health?.checks ? (
            Object.entries(health.checks).map(([name, c]) => (
              <CheckPill key={name} ok={!!c?.ok} label={name} />
            ))
          ) : (
            <span className="muted">Cargando…</span>
          )}
        </div>
        <p className="maint-meta muted">
          Workers: {(queue.workers || []).map((w) => `${w.name} (${w.state})`).join(", ") || "ninguno"}
          {" · "}
          Cola RQ: {queue.queue_length ?? "—"} · En curso: {(queue.started_job_ids || []).length}
          {" · "}
          Fallidos RQ: {(queue.failed_job_ids || []).length}
        </p>
        <div className="maint-stats">
          <span>queued {counts.queued ?? 0}</span>
          <span>processing {counts.processing ?? 0}</span>
          <span>failed {counts.failed ?? 0}</span>
          <span>ready {counts.ready ?? 0}</span>
          <span className={stuck.length ? "warn" : ""}>atascados {stuck.length}</span>
        </div>
      </section>

      <section className="maint-section maint-section-highlight">
        <div className="maint-section-head">
          <h3>Limpieza de temporales</h3>
          <div className="maint-actions">
            <button
              type="button"
              disabled={!!busy}
              onClick={() =>
                purgeTmp({
                  onlyOrphans: true,
                  kinds: ["active", "internal", "external"],
                  busyKey: "purge-tmp",
                  confirmText:
                    "¿Borrar temporales huérfanos (ready/failed/sin vídeo) en todos los discos? No toca jobs activos.",
                })
              }
            >
              {busy === "purge-tmp" ? "Limpiando…" : "Limpiar huérfanos (todos)"}
            </button>
            <button
              type="button"
              className="ghost danger"
              disabled={!!busy}
              onClick={() =>
                purgeTmp({
                  onlyOrphans: false,
                  kinds: ["active", "internal", "external"],
                  busyKey: "purge-tmp-all",
                  confirmText:
                    "¿Borrar TODAS las carpetas temp posibles (excepto queued/processing)? Úsalo si quedó basura de transcodes viejos.",
                })
              }
            >
              {busy === "purge-tmp-all" ? "Limpiando…" : "Limpieza profunda"}
            </button>
          </div>
        </div>
        <p className="maint-meta muted">
          Temp total estimado: {formatBytes(tmpTotalBytes)}
          {tmpOrphanBytes > 0 ? ` · huérfanos ~${formatBytes(tmpOrphanBytes)}` : ""}
        </p>
        <div className="maint-tmp-grid">
          {tmp.length ? (
            tmp.map((t) => (
              <div className="maint-tmp-card" key={`${t.kind}-${t.path}`}>
                <h4>{t.label}</h4>
                <p className="admin-storage-path">{t.path}</p>
                {t.exists ? (
                  <>
                    <p className="maint-meta">
                      {t.video_dirs} carpetas · {t.orphan_dirs} huérfanas · {formatBytes(t.bytes)}
                    </p>
                    <div className="maint-tmp-actions">
                      <button
                        type="button"
                        className="ghost"
                        disabled={!!busy || !t.orphan_dirs}
                        onClick={() =>
                          purgeTmp({
                            onlyOrphans: true,
                            kinds: [t.kind],
                            busyKey: `purge-${t.kind}`,
                            confirmText: `¿Limpiar temporales huérfanos en «${t.label}»?`,
                          })
                        }
                      >
                        {busy === `purge-${t.kind}` ? "…" : "Limpiar huérfanos"}
                      </button>
                      <button
                        type="button"
                        className="ghost danger"
                        disabled={!!busy || !t.video_dirs}
                        onClick={() =>
                          purgeTmp({
                            onlyOrphans: false,
                            kinds: [t.kind],
                            busyKey: `purge-all-${t.kind}`,
                            confirmText: `¿Limpieza profunda en «${t.label}»? Solo se omiten jobs queued/processing.`,
                          })
                        }
                      >
                        Profundo
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="admin-storage-offline">No existe / no montado</p>
                )}
              </div>
            ))
          ) : (
            <p className="muted">Sin datos de temporales.</p>
          )}
        </div>
      </section>

      <section className="maint-section">
        <div className="maint-section-head">
          <h3>Cola de transcode</h3>
          <div className="maint-actions">
            <button
              type="button"
              disabled={!!busy || !stuck.length}
              onClick={() =>
                runAction(
                  "unstick-all",
                  async () => {
                    const r = await api("/api/admin/queue/unstick", {
                      method: "POST",
                      token,
                      body: { all_stuck: true, requeue: true, kill_ffmpeg: true },
                    });
                    notify(`Destrabados ${r.count || 0} job(s) y reencolados.`, null);
                    return r;
                  },
                  `¿Destrabar ${stuck.length} job(s) atascado(s), matar su ffmpeg y reencolarlos?`
                )
              }
            >
              {busy === "unstick-all" ? "Destrabando…" : "Destrabar atascados"}
            </button>
            <button
              type="button"
              className="ghost"
              disabled={!!busy}
              onClick={() =>
                runAction(
                  "clear-failed",
                  async () => {
                    const r = await api("/api/admin/queue/clear-failed", {
                      method: "POST",
                      token,
                      body: {},
                    });
                    notify(`Limpiados ${r.cleared || 0} jobs fallidos de Redis.`, null);
                    return r;
                  },
                  "¿Limpiar el registro de jobs fallidos en Redis?"
                )
              }
            >
              Limpiar fallidos RQ
            </button>
            <button
              type="button"
              className="ghost"
              disabled={!!busy || !(counts.failed > 0)}
              onClick={() =>
                runAction(
                  "requeue-failed",
                  async () => {
                    const r = await api("/api/admin/queue/requeue-failed", {
                      method: "POST",
                      token,
                      body: { limit: 50 },
                    });
                    notify(`Reencolados ${r.count || 0} vídeos fallidos.`, null);
                    return r;
                  },
                  "¿Reencolar hasta 50 vídeos con status failed?"
                )
              }
            >
              Reencolar failed (50)
            </button>
          </div>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table admin-table-striped maint-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Título</th>
                <th>DB</th>
                <th>Job RQ</th>
                <th>Progreso</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.length ? (
                items.map((it) => (
                  <tr key={it.id} className={it.stuck ? "maint-row-stuck" : ""}>
                    <td>{it.id}</td>
                    <td>
                      <div className="maint-title">{it.title}</div>
                      {it.stuck ? <span className="maint-stuck-tag">atascado</span> : null}
                      {it.error_message ? (
                        <div className="muted maint-err">{it.error_message}</div>
                      ) : null}
                    </td>
                    <td>
                      <span className={`badge badge-${it.status}`}>{it.status}</span>
                    </td>
                    <td className="muted">{it.job ? it.job.status : "—"}</td>
                    <td className="muted">
                      {it.progress?.percent != null
                        ? `${it.progress.percent}% ${it.progress.phase || ""}`
                        : "—"}
                    </td>
                    <td className="maint-row-actions">
                      <button
                        type="button"
                        className="ghost"
                        disabled={!!busy}
                        onClick={() =>
                          runAction(
                            `unstick-${it.id}`,
                            async () => {
                              await api("/api/admin/queue/unstick", {
                                method: "POST",
                                token,
                                body: {
                                  video_ids: [it.id],
                                  requeue: true,
                                  kill_ffmpeg: true,
                                },
                              });
                              notify(`Vídeo ${it.id} destrabado y reencolado.`, null);
                            },
                            `¿Destrabar y reencolar el vídeo #${it.id}?`
                          )
                        }
                      >
                        Destrabar
                      </button>
                      <button
                        type="button"
                        className="ghost danger"
                        disabled={!!busy}
                        onClick={() =>
                          runAction(
                            `stop-${it.id}`,
                            async () => {
                              await api("/api/admin/queue/unstick", {
                                method: "POST",
                                token,
                                body: {
                                  video_ids: [it.id],
                                  requeue: false,
                                  kill_ffmpeg: true,
                                },
                              });
                              notify(`Vídeo ${it.id} detenido (failed).`, null);
                            },
                            `¿Detener #${it.id} sin reencolar (quedará failed)?`
                          )
                        }
                      >
                        Detener
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted">
                    No hay vídeos en cola, procesando o fallidos.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="maint-section">
        <div className="maint-section-head">
          <h3>Espacio MinIO</h3>
          <div className="maint-actions">
            <button
              type="button"
              className="ghost"
              disabled={!!busy}
              onClick={() =>
                runAction("purge-orig-dry", async () => {
                  const r = await api("/api/admin/storage/purge-originals", {
                    method: "POST",
                    token,
                    body: { dry_run: true, limit: 500 },
                  });
                  notify(
                    `Simulación: ${r.deleted || 0} originales (~${formatBytes(r.freed_bytes)}).`,
                    null
                  );
                  return r;
                })
              }
            >
              Simular purge originales
            </button>
            <button
              type="button"
              className="danger"
              disabled={!!busy}
              onClick={() =>
                runAction(
                  "purge-orig",
                  async () => {
                    const r = await api("/api/admin/storage/purge-originals", {
                      method: "POST",
                      token,
                      body: { dry_run: false, limit: 500 },
                    });
                    notify(
                      `Borrados ${r.deleted || 0} originales · ${formatBytes(r.freed_bytes)} liberados.`,
                      null
                    );
                    return r;
                  },
                  "¿Borrar originales MinIO de vídeos ya ready? El play usa HLS; no podrás re-transcodificar sin volver a subir."
                )
              }
            >
              Borrar originales ready
            </button>
          </div>
        </div>
        <p className="muted">
          Cuando un vídeo ya tiene HLS, el archivo original en MinIO solo sirve para reprocesar.
          Purgarlo libera mucho espacio en el disco externo.
        </p>
      </section>
    </div>
  );
}
